// frontend/src/components/UI/FloatingAppointmentButton.tsx
import React, { useState, useEffect } from 'react';
import { Fab, Tooltip, useTheme, alpha } from '@mui/material';
import { CalendarToday } from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../hooks/useLanguage';

const MotionFab = motion(Fab);

const FloatingAppointmentButton: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [isVisible, setIsVisible] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      // Show button after scrolling 300px
      setIsVisible(window.scrollY > 300);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleClick = () => {
    navigate('/book-appointment');
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <MotionFab
          color="primary"
          aria-label={t('doctors.bookAppointment')}
          onClick={handleClick}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          initial={{ scale: 0, rotate: -180 }}
          animate={{
            scale: 1,
            rotate: 0,
            boxShadow: isHovered
              ? `0 8px 30px ${alpha(theme.palette.primary.main, 0.5)}`
              : `0 4px 20px ${alpha(theme.palette.primary.main, 0.3)}`,
          }}
          exit={{ scale: 0, rotate: 180 }}
          whileHover={{
            scale: 1.1,
            rotate: 5,
          }}
          whileTap={{ scale: 0.9 }}
          transition={{
            type: 'spring',
            stiffness: 260,
            damping: 20,
          }}
          sx={{
            position: 'fixed',
            bottom: { xs: 16, md: 32 },
            right: { xs: 12, md: 32 },
            zIndex: 1000,
            background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
            width: { xs: 52, md: 64 },
            height: { xs: 52, md: 64 },
            '&:hover': {
              background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.secondary.dark})`,
            },
          }}
        >
          <Tooltip title={t('doctors.bookAppointment')} placement="left" arrow>
            <motion.div
              animate={{ rotate: isHovered ? 360 : 0 }}
              transition={{ duration: 0.6 }}
            >
              <CalendarToday sx={{ fontSize: 28 }} />
            </motion.div>
          </Tooltip>

        </MotionFab>
      )}
    </AnimatePresence>
  );
};

export default FloatingAppointmentButton;
